import { useEffect, useState } from 'react';
import ModuleFilters, { TgetFiltersList } from '../../../../components/ModuleFilters/ModuleFilters';
import { getOrderFilters } from '../../../../services/orders';
import { paymentMethodLabelsValue } from '../../../../utils/variablesToTranslate';
import useOrdersFinancesModule from '../OrdersListingFinances/useOrdersFinancesModule';
import { ISortFilter, SORT_ORDER } from 'types/global';
import './OrdersTrackingFinanceFilters.scss';

const OrdersTrackingFinanceFilters = () => {
  const { setSortFilter, orderFilters, setOrderFilters, sortFilter } = useOrdersFinancesModule();
  const [countriesOptions, setCountriesOptions] = useState<string[]>([]);
  const [openMonthFilter, setOpenMonthFilter] = useState<boolean>(false);
  const [openCompanyFilter, setOpenCompanyFilter] = useState<boolean>(false);
  const [openPaymentMethodFilter, setOpenPaymentMethodFilter] = useState<boolean>(false);
  const [openShipmentCountryFilter, setOpenShipmentCountryFilter] = useState<boolean>(false);
  const [openInvoiceFilter, setOpenInvoiceFilter] = useState<boolean>(false);
  const [openSortFilter, setOpenSortFilter] = useState<boolean>(false);

  const ordersFilterMapping = {
    Empresa: 'company',
    Mes: 'month',
    País: 'countryDestination',
    'Método pago': 'paymentType',
    Factura: 'invoice',
    Ordenar: 'sort'
  };

  const sortOptions = ['Número de la orden', 'Monto'];

  const sortOptionsToValueMap = new Map<string, string>([
    ['Número de la orden', 'orderId'],
    ['Monto', 'totalValue']
  ]);

  const companyFilterRawOptions = ['Empresa 1', 'Empresa 2', 'Empresa 3', 'Empresa 4', 'Empresa 5']; // TODO: Debe venir del backend
  const invoiceFilterRawOptions = ['Pendiente por cargar', 'Cargada']; // TODO: Debe venir del backend
  const monthFilterRawOptions = [
    'Enero 2025',
    'Febrero 2025',
    'Marzo 2025',
    'Abril 2025',
    'Mayo 2025',
    'Junio 2025',
    'Julio 2025',
    'Agosto 2025',
    'Septiembre 2025',
    'Octubre 2025',
    'Noviembre 2025',
    'Diciembre 2025'
  ];

  const monthToNumberMap: Map<string, string> = monthFilterRawOptions
    ? new Map(monthFilterRawOptions?.map((month, i) => [month, i + 1]))
    : new Map();

  const companyFilterRawOptionsToNumberMap: Map<string, string> = companyFilterRawOptions
    ? new Map(companyFilterRawOptions?.map((company, i) => [company, i + 1]))
    : new Map();

  const invoiceFilterRawOptionsToNumberMap: Map<string, string> = invoiceFilterRawOptions.reduce(
    (acc, invoice, index) => {
      acc.set(invoice, String(index + 1));
      return acc;
    },
    new Map<string, string>()
  );

  const paymentMethodRawOptionName = paymentMethodLabelsValue.map(item => item.name);

  const paymentMethodNameValue: Map<string, string> = new Map(
    paymentMethodLabelsValue?.map(type => [type.name, type.value])
  );

  const getOrdersFiltersList: TgetFiltersList = (formModuleFilters, handleClickFilterOption, getFilterActiveStatus) => [
    {
      title: 'Empresa',
      openFunction: setOpenCompanyFilter,
      openStatus: openCompanyFilter,
      rawSelectOptions: formModuleFilters('Empresa', companyFilterRawOptions, companyFilterRawOptionsToNumberMap),
      handleChangeSelectedOption: handleClickFilterOption('company', 'value'),
      isFilterActive: getFilterActiveStatus('company'),
      showSearch: true
    },
    {
      title: 'Mes',
      openFunction: setOpenMonthFilter,
      openStatus: openMonthFilter,
      rawSelectOptions: formModuleFilters('Mes', monthFilterRawOptions, monthToNumberMap),
      handleChangeSelectedOption: handleClickFilterOption('month', 'value'),
      isFilterActive: getFilterActiveStatus('month'),
      showSearch: true,
      searchPlaceholder: 'Buscador'
    },
    {
      title: 'País',
      openFunction: setOpenShipmentCountryFilter,
      openStatus: openShipmentCountryFilter,
      rawSelectOptions: formModuleFilters('País', countriesOptions),
      handleChangeSelectedOption: handleClickFilterOption('countryDestination', 'name'),
      isFilterActive: getFilterActiveStatus('countryDestination'),
      showSearch: true,
      searchPlaceholder: 'Buscador',
      sortAlphabetically: true
    },
    {
      title: 'Factura',
      openFunction: setOpenInvoiceFilter,
      openStatus: openInvoiceFilter,
      rawSelectOptions: formModuleFilters('Factura', invoiceFilterRawOptions, invoiceFilterRawOptionsToNumberMap),
      handleChangeSelectedOption: handleClickFilterOption('invoice', 'value'),
      isFilterActive: getFilterActiveStatus('invoice')
    },
    {
      title: 'Método pago',
      openFunction: setOpenPaymentMethodFilter,
      openStatus: openPaymentMethodFilter,
      rawSelectOptions: formModuleFilters('Método pago', paymentMethodRawOptionName, paymentMethodNameValue),
      handleChangeSelectedOption: handleClickFilterOption('paymentType', 'value'),
      isFilterActive: getFilterActiveStatus('paymentType')
    },
    {
      title: 'Ordenar',
      openFunction: setOpenSortFilter,
      openStatus: openSortFilter,
      rawSelectOptions: formModuleFilters('Ordenar', sortOptions, sortOptionsToValueMap),
      handleChangeSelectedOption: handleClickFilterOption('sort', 'value'),
      isFilterActive: getFilterActiveStatus('sort'),
      titleFilterContainer: 'Ordenar por:',
      useRadioButtons: true
    }
  ];

  const getShipmentCountryFilterOptions = async () => {
    const response = await getOrderFilters();
    if (response?.countries) setCountriesOptions(response?.countries);
  };

  const handleSortChange = (sortFilter: ISortFilter) => {
    setSortFilter(sortFilter);
  };

  useEffect(() => {
    getShipmentCountryFilterOptions();
  }, []);

  useEffect(() => {
    if (!orderFilters.some(filter => filter.name === 'sort')) {
      setOrderFilters([
        ...orderFilters,
        {
          name: 'sort',
          includes: 'in',
          values: ['orderId']
        }
      ]);

      setSortFilter({ value: SORT_ORDER.DESC });
    }
  }, [setOrderFilters, setSortFilter, orderFilters]);

  return (
    <div className="ordersTrackingFinanceFilters">
      <ModuleFilters
        moduleFilters={orderFilters}
        setModuleFilters={setOrderFilters}
        getFiltersList={getOrdersFiltersList}
        moduleFiltersTitlesMapping={ordersFilterMapping}
        customGap={32}
        optionTextCustomClass="optionTextCustomClassBussines"
        showSort
        showSortInFilter="Ordenar"
        onSortChange={handleSortChange}
        singleSelectFilters={{ sort: true }}
        customHeightContainer="customHeightContainerOrdersTrackingFinance"
        showReferenceLetters
        customSortClassName="customSortClassNameOrdersTrackingFinance"
        currentSortFilter={sortFilter}
      />
    </div>
  );
};

export default OrdersTrackingFinanceFilters;
